import { ee, ensureEarthEngineReady } from '../../services/earth-engine';
import * as MapUtils from '../../common/map';
import * as Metadata from '../../common/metadata';
import { generateColors, expandTransectToRectangle } from '../../common/utils';
import { getImageKey } from '../../app/components/visualization/imageCsv';
import { Actions as Acquisition } from '../../store/ducks/acquisition';
import { Actions as Results } from '../../store/ducks/results';
import { Actions as Map } from '../../store/ducks/map';
import { aoiGeometry } from '../../common/aoi';

export const SESSION_EXPORT_VERSION = 1;
export const SESSION_EXPORT_MODULE = 'shoreline';

const PENDING_SHAPES_STORAGE_KEY = 'shorelineSessionImportShapes';
const PENDING_BASELINE_STORAGE_KEY = 'shorelineSessionImportBaseline';

/**
 * @function buildMissionsFromImages
 * @description Regroups a flat image list (as found in `chosenImages`, each entry already
 * shaped like `uniteMissionsDates`'s output: `{name, shortname, date, content}`) back into the
 * per-mission `{name, shortname, dates: {[date]: cloudPercentage0to100}}` structure
 * `state.acquisition.missions` normally holds. `PeriodImageChooser` builds its candidate table
 * via `uniteMissionsDates(missions)`, not from `availableDates` directly, so without this the
 * table renders empty even though `availableDates` itself is populated.
 * @param {Array<Object>} images - Flat image entries (`chosenImages` shape).
 * **Called by:
 * @see {importSessionState}
 * @memberof module:Shoreline
 * @returns {Array<Object>} Per-mission grouped dates, ready for `uniteMissionsDates`.
 */
const buildMissionsFromImages = (images) => {
  const byName = {};
  images.forEach((img) => {
    if (!byName[img.name]) byName[img.name] = { name: img.name, shortname: img.shortname || img.name, dates: {} };
    byName[img.name].dates[img.date] = (img.content ?? 0) * 100;
  });
  return Object.values(byName);
};

/**
 * @function buildSessionExport
 * @description Bundles everything needed to redraw a completed Shoreline analysis without
 * re-running it: the AOI/satellite/selected images (so the page context is right) and the
 * already-evaluated shoreline/transect results (so nothing needs to hit Earth Engine again on
 * import). Meant purely as a developer/testing shortcut, so QA doesn't have to redo a full
 * analysis every time they need a completed result to test against.
 * @param {Object} params
 * @param {Number} params.satelliteIndex - `state.acquisition.satelliteIndex`.
 * @param {Array<Array<Number>>} params.coordinates - AOI coordinates, `state.acquisition.coordinates`.
 * @param {Array<Object>} params.chosenImages - `state.results.chosenImages`.
 * @param {Array<Array<Number>>} params.baselineCoords - `state.results.baselineData.coords`.
 * @param {Array<Object>} params.transectData - `state.results.coastlineData.transectData` (already plain).
 * @param {ee.List} params.shorelineData - `state.results.coastlineData.shorelineData` (live EE list; evaluated here).
 * **Called by:
 * @see {CoastlineEvolutionDialog} - via the "export session for testing" menu item.
 * @memberof module:Shoreline
 * @returns {Object} A plain, JSON-serializable session snapshot.
 */
export const buildSessionExport = ({
  satelliteIndex,
  coordinates,
  chosenImages,
  baselineCoords,
  transectData,
  shorelineData,
}) => ({
  version: SESSION_EXPORT_VERSION,
  module: SESSION_EXPORT_MODULE,
  exportedAt: new Date().toISOString(),
  satelliteIndex,
  satelliteType: window.sessionStorage.getItem('typeSatellite') || 'TOA',
  cloudCoverage: Number(window.sessionStorage.getItem('acquisitionCloudCoverage')) || 0.05,
  coordinates,
  chosenImages,
  baselineCoords,
  transectData,
  shorelineFeatures: shorelineData.getInfo(),
});

/**
 * @function importSessionState
 * @description Rehydrates a session exported by `buildSessionExport` straight into
 * `state.acquisition`/`state.results`, and stashes the evaluated shoreline/transect features in
 * `sessionStorage` for `renderPendingImportedShapes` to turn into map shapes once the result
 * page's map exists. No Earth Engine calls are made along this path — reconstructing `ee.Feature`/
 * `ee.Geometry`/`ee.List` from already-evaluated data is a purely local, client-side graph build;
 * only a later `.getInfo()`/`.evaluate()` would hit the server, and this never calls one.
 * `shorelineFeatures` are wrapped back into an `ee.List` before being stored in `coastlineData`
 * since `CoastlineEvolutionDialog`'s own exports (shapefile, etc.) call `.getInfo()` on it
 * expecting a live EE object.
 * @param {Function} dispatch - Redux dispatch.
 * @param {Object} data - A session snapshot produced by `buildSessionExport`.
 * @see {@link buildSessionExport}
 * @see {@link renderPendingImportedShapes}
 * **Called by:
 * @see {Shoreline} - via the "import session for testing" file input.
 * @memberof module:Shoreline
 * @returns {void}
 */
export const importSessionState = async (dispatch, data) => {
  await ensureEarthEngineReady();
  dispatch(Acquisition.reset());
  const {
    satelliteIndex,
    satelliteType,
    cloudCoverage,
    coordinates,
    chosenImages,
    baselineCoords,
    transectData,
    shorelineFeatures,
  } = data;

  dispatch(Acquisition.setSatellite(satelliteIndex, satelliteType || 'TOA'));
  dispatch(Acquisition.setAOI(null, coordinates, aoiGeometry(coordinates), null));

  dispatch(Acquisition.setAvailableDates(chosenImages, buildMissionsFromImages(chosenImages)));
  if (chosenImages.length > 0) {
    const sortedDates = [...chosenImages].map((img) => img.date).sort();
    dispatch(Acquisition.setPeriod(sortedDates[0], sortedDates[sortedDates.length - 1]));
  }

  window.sessionStorage.setItem('acquisitionCloudCoverage', cloudCoverage || 0.05);
  const selection = {};
  chosenImages.forEach((img) => {
    selection[getImageKey(img)] = true;
  });
  window.sessionStorage.setItem('acquisitionImageSelection', JSON.stringify(selection));

  dispatch(Results.push('chosenImages', chosenImages));
  dispatch(
    Results.push('baselineData', {
      coords: baselineCoords,
      baseline: ee.Geometry.LineString(baselineCoords),
    }),
  );

  if (baselineCoords && baselineCoords.length >= 2) {
    window.sessionStorage.setItem(PENDING_BASELINE_STORAGE_KEY, JSON.stringify(baselineCoords));
  } else {
    window.sessionStorage.removeItem(PENDING_BASELINE_STORAGE_KEY);
  }

  const shorelineData = ee.List(
    shorelineFeatures.map((feature) => ee.Feature(ee.Geometry(feature.geometry), feature.properties)),
  );
  dispatch(Results.push('coastlineData', { transectData, shorelineData }));

  window.sessionStorage.setItem(
    PENDING_SHAPES_STORAGE_KEY,
    JSON.stringify({ shorelineFeatures, transectData, imageCount: chosenImages.length }),
  );
};

/**
 * @function restorePendingBaseline
 * @description If `importSessionState` left a baseline waiting, draws it as a real
 * `google.maps.Polyline` and returns a `{overlay}` object shaped exactly like
 * `CoastlineAnalysisSteps`'s own `handleDrawing`/`baseLine` state, then clears the pending flag.
 * Returns `null` (a no-op) if nothing is pending, so it's safe to call unconditionally.
 * @param {google.maps.Map} map - The now-ready map instance to draw onto.
 * @see {@link importSessionState}
 * **Called by:
 * @see {ProcessingPage} - Shoreline's result page, via `GoogleMap`'s `onLoad`.
 * @memberof module:Shoreline
 * @returns {{overlay: google.maps.Polyline}|null}
 */
export const restorePendingBaseline = (map) => {
  const raw = window.sessionStorage.getItem(PENDING_BASELINE_STORAGE_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(PENDING_BASELINE_STORAGE_KEY);

  let coords;
  try {
    coords = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(coords) || coords.length < 2) return null;

  const overlay = new window.google.maps.Polyline({
    path: coords.map(([lng, lat]) => ({ lat, lng })),
    strokeColor: '#03d4f0ff',
    strokeOpacity: 1,
    strokeWeight: 3,
  });
  overlay.setMap(map);
  return { overlay };
};

/**
 * @function renderPendingImportedShapes
 * @description If `importSessionState` left shoreline/transect features waiting (see its doc
 * comment for why), turns them into map shapes now — via `MapUtils.addShape`, the same
 * client-side helper `Map.addEEFeature`'s saga uses once it already has evaluated GeoJSON — and
 * clears the pending flag. A no-op if nothing is pending, so it's safe to call unconditionally.
 * @param {Function} dispatch - Redux dispatch.
 * @see {@link importSessionState}
 * **Called by:
 * @see {ProcessingPage} - Shoreline's result page, via `GoogleMap`'s `onLoad` (once the map
 * singleton `MapUtils.addShape` writes to actually exists).
 * @memberof module:Shoreline
 * @returns {void}
 */
export const renderPendingImportedShapes = (dispatch) => {
  const raw = window.sessionStorage.getItem(PENDING_SHAPES_STORAGE_KEY);
  if (!raw) return;
  window.sessionStorage.removeItem(PENDING_SHAPES_STORAGE_KEY);

  const { shorelineFeatures, transectData, imageCount } = JSON.parse(raw);

  dispatch(Map.removeShapeGroup('coastlineData'));
  dispatch(Map.removeShapeGroup(Metadata.FeatureType.TRANSECT));

  const shoreColors = generateColors(Math.max(imageCount, 1), 66);
  const shorelineShapes = shorelineFeatures.map((feature, i) =>
    MapUtils.addShape(feature, shoreColors[i % shoreColors.length], 1, 'coastlineData'),
  );
  dispatch(Map.addShapes(shorelineShapes, 'forms.map.shorelines', 'coastlineData', shorelineFeatures));

  const transectShapes = transectData.map((feature) => {
    const color = Metadata.ESTEVES_LABELS[feature.properties?.label]?.color || '#FF0000';
    return MapUtils.addShape(expandTransectToRectangle(feature), color, 1, Metadata.FeatureType.TRANSECT);
  });
  dispatch(Map.addShapes(transectShapes, 'forms.map.transects.title', Metadata.FeatureType.TRANSECT, transectData));
};
